import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import { useProductList, useProductFamilies } from './hooks/useProductMeta';

// Prefer the structured metadata fields; fall back to parsing the name/notes
// for legacy records that only have a descriptive name.
function deriveFacets(p) {
  const text = `${p.name || ''} ${p.notes || ''}`;
  const lc = text.toLowerCase();

  let size = p.size || '';
  if (!size) { const m = text.match(/(\d{2}(?:\.\d)?)\s*["”]/); if (m) size = `${m[1]}"`; }

  let touch = '';
  if (typeof p.touch === 'boolean') touch = p.touch ? 'Touch' : 'Non-touch';
  else if (/\bnon[\s-]?touch\b|\bno[\s-]touch\b/.test(lc)) touch = 'Non-touch';
  else if (/multitouch/.test(lc)) touch = 'Multitouch';
  else if (/touch/.test(lc)) touch = 'Touch';

  let power = p.powerSupply || '';
  if (!power) {
    if (/ac\/dc/.test(lc)) power = 'AC/DC';
    else if (/ac\/ac/.test(lc)) power = 'AC/AC';
    else if (/dc\/dc|dual dc/.test(lc)) power = 'DC/DC';
    else if (/\bac\b/.test(lc) && /\bdc\b/.test(lc)) power = 'AC/DC';
    else if (/\bac\b/.test(lc)) power = 'AC';
    else if (/\bdc\b/.test(lc)) power = 'DC';
  }

  let bonding = p.bonding || '';
  if (!bonding) {
    if (/optical bond/.test(lc)) bonding = 'Optical';
    else if (/air bond/.test(lc)) bonding = 'Air';
  }

  const brightness = p.brightness || (/high.?bright|\bhb\b/i.test(text) ? 'High bright' : '');

  return { size, touch, power, bonding, brightness };
}

const FACETS = [
  { key: 'size', label: 'Size' },
  { key: 'touch', label: 'Touch' },
  { key: 'power', label: 'Power supply' },
  { key: 'bonding', label: 'Bonding' },
  { key: 'brightness', label: 'Brightness' },
];

const PRINT_CSS = `
@media print {
  .no-print { display: none !important; }
  .sheet-print, .sheet-print * { color: #000 !important; background: #fff !important; box-shadow: none !important; }
  .sheet-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  .sheet-table th, .sheet-table td { border: 1px solid #888; padding: 3px 6px; text-align: left; }
  @page { margin: 12mm; }
}
`;

export default function ProductSheetPage() {
  const { isSignedIn } = useUser();
  const [family, setFamily] = useState('');
  const [type, setType] = useState('MON'); // complete products by default
  const [sel, setSel] = useState({}); // { facetKey: Set(values) }
  const { families } = useProductFamilies({ enabled: !!isSignedIn });
  const { items, isLoading, error } = useProductList({ family, type, limit: 1000, enabled: !!isSignedIn });

  const rows = useMemo(() => items.map(p => ({ ...p, facets: deriveFacets(p) })), [items]);

  // Available values per facet, from the loaded rows.
  const facetValues = useMemo(() => {
    const out = {};
    for (const f of FACETS) {
      out[f.key] = Array.from(new Set(rows.map(r => r.facets[f.key]).filter(Boolean))).sort();
    }
    return out;
  }, [rows]);

  const toggle = (key, val) => setSel(prev => {
    const set = new Set(prev[key] || []);
    if (set.has(val)) set.delete(val); else set.add(val);
    return { ...prev, [key]: set };
  });
  const clearAll = () => setSel({});

  const filtered = rows.filter(r =>
    FACETS.every(f => {
      const set = sel[f.key];
      return !set || set.size === 0 || set.has(r.facets[f.key]);
    })
  );

  const activeCount = Object.values(sel).reduce((n, s) => n + (s ? s.size : 0), 0);

  if (!isSignedIn) {
    return <div className="ni-page"><div className="ni-shell"><div className="ni-panel ni-empty">Please sign in.</div></div></div>;
  }

  return (
    <div className="ni-page sheet-print">
      <style>{PRINT_CSS}</style>
      <div className="ni-shell">
        <header className="ni-header no-print">
          <div className="ni-brand">
            <Link to="/products" title="Back to products" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            </Link>
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Sales Sheet</span>
              <span className="ni-brand-subtitle">Pick a family + characteristics → the matching product number</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to="/products" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', textDecoration: 'none' }}>← Products</Link>
            <button type="button" className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }} onClick={() => window.print()}>🖨 Print</button>
          </div>
        </header>

        {/* Printable title (hidden on screen, shown on paper) */}
        <h1 style={{ display: 'none' }} className="only-print">North Invent — Product Sheet</h1>

        {/* Filters */}
        <div className="ni-panel no-print" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
            <select value={family} onChange={e => setFamily(e.target.value)} className="ni-select" style={{ minWidth: 180 }}>
              <option value="">All families</option>
              {families.map(f => <option key={f.familyKey} value={f.familyKey}>{f.profile?.displayName || f.familyKey} ({f.productCount})</option>)}
            </select>
            <select value={type} onChange={e => setType(e.target.value)} className="ni-select" style={{ minWidth: 160 }} title="Product type">
              <option value="MON">MON · complete products</option>
              <option value="DIU">DIU</option>
              <option value="ELU">ELU</option>
              <option value="">All types</option>
            </select>
            {activeCount > 0 && (
              <button type="button" className="ni-button ni-button--ghost" style={{ fontSize: '0.8rem' }} onClick={clearAll}>Clear filters ({activeCount})</button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: colors.textMuted }}>{filtered.length} of {rows.length} match</span>
          </div>

          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
            {FACETS.filter(f => facetValues[f.key].length > 0).map(f => (
              <div key={f.key} style={{ minWidth: 130 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>{f.label}</div>
                {facetValues[f.key].map(v => {
                  const checked = !!(sel[f.key] && sel[f.key].has(v));
                  return (
                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: colors.textBody, cursor: 'pointer', padding: '0.1rem 0' }}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(f.key, v)} />
                      {v}
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="ni-panel no-print" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><LoadingSpinner size={48} /></div>
        ) : error ? (
          <div className="ni-panel ni-empty">Failed to load. {error.message}</div>
        ) : (
          <div className="ni-panel" style={{ overflowX: 'auto', padding: '0.5rem' }}>
            <table className="sheet-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: colors.textMuted }}>
                  <th style={thS}>Product №</th>
                  <th style={thS}>Family</th>
                  <th style={thS}>Size</th>
                  <th style={thS}>Touch</th>
                  <th style={thS}>Power</th>
                  <th style={thS}>Bonding</th>
                  <th style={thS}>Bright</th>
                  <th style={thS}>Status</th>
                  <th style={thS}>Name</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.productNumber} style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
                    <td style={{ ...tdS, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <Link to={`/product/${encodeURIComponent(r.productNumber)}`} style={{ color: colors.accent, textDecoration: 'none' }}>{r.productNumber}</Link>
                    </td>
                    <td style={tdS}>{r.family || '—'}</td>
                    <td style={tdS}>{r.facets.size || '—'}</td>
                    <td style={tdS}>{r.facets.touch || '—'}</td>
                    <td style={tdS}>{r.facets.power || '—'}</td>
                    <td style={tdS}>{r.facets.bonding || '—'}</td>
                    <td style={tdS}>{r.facets.brightness || '—'}</td>
                    <td style={tdS}>{r.status || '—'}</td>
                    <td style={{ ...tdS, color: colors.textBody }}>{r.name || '—'}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ ...tdS, textAlign: 'center', color: colors.textMuted }}>No products match these characteristics.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const thS = { padding: '0.4rem 0.5rem', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' };
const tdS = { padding: '0.4rem 0.5rem', color: colors.textMuted, verticalAlign: 'top' };
